import { useState } from "react";
import { FatalDate, type RelatedFatal } from "@/ui/kit";
import { FlowsInProgress } from "@/ui/bpm";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { navigate, useTitle } from "@/lib/router";
import { addDaysIso, fmtDateTime, fmtMoney, isOverdue, STATE_LABEL, todayIso } from "@/lib/format";
import { useLookups } from "@/lib/catalog";
import { Badge, Card, Empty, ErrorBox, Loading } from "@/ui/kit";
import { Avatar, Icon } from "@/ui/icons";
import { HBars } from "@/ui/charts";
import { toast } from "@/ui/feedback";
import { GoalCard } from "./IndicatorsPage";
import { AgendaCard, MiniCalendar, QuickCard, TasksCard, TipCard, type BlocksData } from "./SocioView";
import { keepPreviousData } from "@tanstack/react-query";
import { useRef } from "react";
import { bottom, compact, COLS, GAP, nudge, px, resolve, ROW } from "@/lib/grid-layout";
import { ChatWidget, PostitsWidget, QUERY_WIDGETS, QueryWidget } from "@/ui/home-widgets";
import { ScoreRanking } from "./SettingsScoring";

const BLOCK_WIDGETS: WidgetId[] = ["compromissos", "calendario", "tarefas"];

function CalendarWidget(): JSX.Element {
  const [month, setMonth] = useState<string | null>(null);
  const { data } = useQuery({ queryKey: ["painel-blocos", month], queryFn: () => api.get<BlocksData>(`/painel/blocos${month ? `?mes=${month}` : ""}`), placeholderData: keepPreviousData });
  return <section className="card">{!data ? <Loading /> : <MiniCalendar month={data.month} today={data.today} marks={data.calendar} onMonth={setMonth} />}</section>;
}

interface PanelAct {
  id: string; number: number; title: string; state: string; internalDueAt: string | null; fatalDueAt: string | null;
  plannedAt: string | null; priority: string; processId: string | null; typeKey: string | null; responsibleUserId: string | null;
  relatedFatal?: RelatedFatal | null;
  firstStartedAt?: string | null;
}
interface Panel {
  user: { id: string; displayName: string };
  today: string;
  counts: { mineOpen: number; mineOverdue: number; mineToday: number; reviewQueue: number; unassigned: number; staleProcesses: number };
  alerts: Array<{ level: "vermelho" | "amarelo"; text: string; link: string }>;
  mine: PanelAct[];
  fatalDeadlines: PanelAct[];
  hearings: PanelAct[];
  controladoria: { pending: number; late: number; captureOk: boolean; lastRunAt: string | null; lastOkAt: string | null } | null;
  birthdays: Array<{ id: string; name: string; birthDate: string }>;
  financeiro?: { receivableOverdue: string; receivableOverdueCount: number; payableNext7: string; payableNext7Count: number };
  canSeeFinance: boolean;
}

const WD = ["dom", "seg", "ter", "qua", "qui", "sex", "sáb"];
const MES = ["jan", "fev", "mar", "abr", "mai", "jun", "jul", "ago", "set", "out", "nov", "dez"];

function greeting(): string {
  const h = new Date().getHours();
  return h < 12 ? "Bom dia" : h < 18 ? "Boa tarde" : "Boa noite";
}

export function DateChip({ iso }: { iso: string }): JSX.Element {
  const d = new Date(`${iso.slice(0, 10)}T12:00:00Z`);
  const today = iso.slice(0, 10) === todayIso();
  return (
    <div className={`date-chip ${today || iso.slice(0, 10) < todayIso() ? "hot" : ""}`}>
      <div className="d">{d.getUTCDate()}</div>
      <div className="m">{today ? "hoje" : WD[d.getUTCDay()]}</div>
    </div>
  );
}

const stateTone: Record<string, "info" | "warn" | "neutral" | "ok"> = { em_andamento: "warn", em_revisao: "info", atribuida: "neutral", concluida: "ok" };

/** Barras por dia útil da semana: atividades com data + prazos fatais. */
function WeekLoad({ items, fatal }: { items: PanelAct[]; fatal: PanelAct[] }): JSX.Element {
  const today = todayIso();
  const wd = new Date(`${today}T12:00:00Z`).getUTCDay() || 7;
  const monday = addDaysIso(today, 1 - wd);
  const days = Array.from({ length: 5 }, (_, i) => addDaysIso(monday, i));
  const dayOf = (a: PanelAct) => (a.plannedAt ?? a.internalDueAt ?? a.fatalDueAt ?? "").slice(0, 10);
  const data = days.map((d) => ({
    d, all: items.filter((a) => dayOf(a) === d).length, fatal: fatal.filter((a) => (a.fatalDueAt ?? "").slice(0, 10) === d).length,
  }));
  const max = Math.max(3, ...data.map((x) => x.all + x.fatal));
  const H = 110;
  return (
    <svg viewBox="0 0 560 150" width="100%" height="150" style={{ display: "block" }}>
      <line x1="20" y1={H + 10} x2="545" y2={H + 10} stroke="var(--border)" />
      {data.map((x, i) => {
        const cx = 60 + i * 110;
        const hAll = (x.all / max) * H;
        const hF = (x.fatal / max) * H;
        const isToday = x.d === today;
        return (
          <g key={x.d} style={{ cursor: "pointer" }} onClick={() => navigate(`/agenda?modo=dia&data=${x.d}`)}>
            <rect x={cx} y={H + 10 - hAll - hF} width="46" height={Math.max(hAll + hF, 2)} rx="5" fill="var(--primary)" opacity={isToday ? 1 : 0.8} />
            {hF > 0 && <rect x={cx} y={H + 10 - hF} width="46" height={hF} fill="var(--red)" />}
            <text x={cx + 23} y={H + 10 - hAll - hF - 6} textAnchor="middle" fontSize="11" fill="var(--muted)">{x.all + x.fatal || ""}</text>
            <text x={cx + 23} y={H + 28} textAnchor="middle" fontSize="11" fill={isToday ? "var(--text)" : "var(--muted)"} fontWeight={isToday ? 700 : 400}>
              {WD[(i + 1) % 7]} {Number(x.d.slice(8))}{isToday ? " · hoje" : ""}
            </text>
          </g>
        );
      })}
    </svg>
  );
}

type WidgetId = "alertas" | "kpis" | "fatais" | "carga_semana" | "minhas" | "audiencias" | "captura" | "aniversarios" | "financeiro" | "metas" | "equipe_carga" | "processos_etapa"
  | "compromissos" | "calendario" | "tarefas" | "acesso_rapido" | "dica"
  | "fin_saldo" | "fin_receber" | "fin_pagar" | "fin_inadimplencia" | "fin_fluxo" | "fin_meta" | "fin_repasses" | "fin_recibos"
  | "intimacoes" | "parados" | "revisoes" | "atrasadas_equipe" | "casos_mes" | "captacao" | "postits" | "chat"
  | "kpi_abertas" | "kpi_atrasadas" | "kpi_fatais" | "kpi_revisar" | "pontuacao" | "fluxos";
interface Item { id: WidgetId; x: number; y: number; w: number; h: number; o?: Record<string, string | number | boolean> }
interface Layout { v: 3; items: Item[]; source?: string }
/** Tamanhos prontos (colunas × linhas de 20 px). */
const PRESETS: Array<[string, string, number, number]> = [["P", "Pequeno", 6, 6], ["M", "Médio", 12, 12], ["G", "Grande", 24, 16]];
const SMALL: WidgetId[] = ["kpi_abertas", "kpi_atrasadas", "kpi_fatais", "kpi_revisar"];
const WIDGET_LABEL: Record<WidgetId, string> = {
  alertas: "Alertas", kpis: "Números do dia (antigo)",
  kpi_abertas: "Minhas abertas", kpi_atrasadas: "Atrasadas", kpi_fatais: "Prazos fatais na semana", kpi_revisar: "Para eu revisar", fatais: "Prazos fatais — 7 dias", carga_semana: "Carga da semana", minhas: "Minhas atividades",
  audiencias: "Audiências", captura: "Captura de publicações", aniversarios: "Aniversariantes", financeiro: "Financeiro (resumo)",
  metas: "Metas do período", equipe_carga: "Carga da equipe (10 dias)", processos_etapa: "Processos por etapa",
  compromissos: "Meus compromissos", calendario: "Calendário do mês", tarefas: "Tarefas em aberto", acesso_rapido: "Acesso rápido", dica: "Dica do sistema",
  postits: "Meus post-its", chat: "Chat — mensagens", pontuacao: "Pontuação do mês", fluxos: "Fluxos em andamento",
  fin_saldo: QUERY_WIDGETS.fin_saldo!.label, fin_receber: QUERY_WIDGETS.fin_receber!.label, fin_pagar: QUERY_WIDGETS.fin_pagar!.label,
  fin_inadimplencia: QUERY_WIDGETS.fin_inadimplencia!.label, fin_fluxo: QUERY_WIDGETS.fin_fluxo!.label, fin_meta: QUERY_WIDGETS.fin_meta!.label,
  fin_repasses: QUERY_WIDGETS.fin_repasses!.label, fin_recibos: QUERY_WIDGETS.fin_recibos!.label, intimacoes: QUERY_WIDGETS.intimacoes!.label,
  parados: QUERY_WIDGETS.parados!.label, revisoes: QUERY_WIDGETS.revisoes!.label, atrasadas_equipe: QUERY_WIDGETS.atrasadas_equipe!.label,
  casos_mes: QUERY_WIDGETS.casos_mes!.label, captacao: QUERY_WIDGETS.captacao!.label,
};
const WIDGET_GROUP = (w: WidgetId): string => QUERY_WIDGETS[w]?.group ?? (SMALL.includes(w) ? "Números do dia" : w === "financeiro" ? "Financeiro" : ["postits", "chat", "acesso_rapido", "dica", "alertas", "kpis"].includes(w) ? "Geral" : ["metas", "equipe_carga", "processos_etapa", "aniversarios", "pontuacao", "fluxos"].includes(w) ? "Escritório" : "Minha rotina");
const DEFAULT_SIZE = (w: WidgetId): [number, number] => (SMALL.includes(w) ? [6, 2] : w === "alertas" || w === "dica" ? [24, 4] : [8, 12]);

function MetasWidget(): JSX.Element {
  const { data } = useQuery({ queryKey: ["bi-metas", "inicio"], queryFn: () => api.get<{ goals: Parameters<typeof GoalCard>[0]["g"][] }>("/bi/metas") });
  return (
    <Card title="Metas do período" actions={<button className="link-btn" onClick={() => navigate("/indicadores?aba=metas")}>Ver todas</button>}>
      {!data ? <Loading /> : data.goals.length ? <div className="stack gap-8">{data.goals.slice(0, 4).map((g) => <GoalCard key={g.id} g={g} compact />)}</div>
        : <Empty icon="flag">Nenhuma meta definida.</Empty>}
    </Card>
  );
}

function TeamLoadWidget(): JSX.Element {
  const { data } = useQuery({ queryKey: ["bi-carga-curta"], queryFn: () => api.get<{ days: string[]; users: Array<{ id: string; name: string; perDay: Record<string, number> }> }>("/bi/equipe/carga-curta") });
  if (!data) return <Card title="Carga da equipe"><Loading /></Card>;
  const max = Math.max(1, ...data.users.flatMap((u) => Object.values(u.perDay)));
  const heat = (n: number) => (!n ? "h0" : n / max < 0.34 ? "h1" : n / max < 0.67 ? "h2" : "h3");
  return (
    <Card title="Carga da equipe — próximos 10 dias úteis" actions={<button className="link-btn" onClick={() => navigate("/indicadores?aba=equipe")}>Indicadores</button>}>
      <div className="heat-wrap">
        <table className="heat">
          <thead><tr><th />{data.days.map((d) => <th key={d}>{"DSTQQSS"[new Date(`${d}T12:00:00Z`).getUTCDay()]}<br />{d.slice(8, 10)}</th>)}</tr></thead>
          <tbody>{data.users.map((u) => (
            <tr key={u.id}><td className="who"><b>{u.name}</b></td>
              {data.days.map((d) => { const n = u.perDay[d] ?? 0; return <td key={d} className={`cell ${heat(n)}`} style={{ cursor: "default" }} title={`${n} atividade(s)`}>{n || ""}</td>; })}
            </tr>
          ))}</tbody>
        </table>
      </div>
    </Card>
  );
}

function StagesWidget(): JSX.Element {
  const { data } = useQuery({ queryKey: ["bi-processos", "inicio"], queryFn: () => api.get<{ processStages: Array<{ name: string; count: number; color: string }> }>("/bi/processos") });
  return (
    <Card title="Processos por etapa" actions={<button className="link-btn" onClick={() => navigate("/processos?modo=quadro")}>Quadro</button>}>
      {!data ? <Loading /> : <HBars rows={data.processStages.filter((s) => s.count).map((s) => ({ name: s.name, value: s.count, color: s.color }))} />}
    </Card>
  );
}

export function PanelPage(): JSX.Element {
  useTitle("Início");
  const [showFin, setShowFin] = useState(false);
  const { data, error, isLoading } = useQuery({
    queryKey: ["painel", showFin],
    queryFn: () => api.get<Panel>(`/painel${showFin ? "?financeiro=1" : ""}`),
    refetchInterval: 120_000,
  });
  const { data: layout } = useQuery({ queryKey: ["painel-layout"], queryFn: () => api.get<Layout>("/painel/layout"), staleTime: 60_000 });
  const needBlocks = !!layout && layout.items.some((it) => BLOCK_WIDGETS.includes(it.id));
  const { data: blocks } = useQuery({ queryKey: ["painel-blocos", null], enabled: needBlocks, queryFn: () => api.get<BlocksData>("/painel/blocos"), refetchInterval: 120_000 });
  const { userName, catalog, can } = useLookups();
  const [edit, setEdit] = useState<Layout | null>(null);
  const [roleTarget, setRoleTarget] = useState("");
  const qc = useQueryClient();
  const typeName = (key: string | null) => (key ? catalog?.activityTypes.find((t) => t.key === key)?.name ?? "" : "");

  if (isLoading || !layout) return <Loading lines={6} />;
  if (error || !data) return <ErrorBox error={error} />;
  const first = data.user.displayName.split(" ")[0];
  const now = new Date();

  const available = (w: WidgetId): boolean => {
    if (w === "financeiro" || w.startsWith("fin_")) return data.canSeeFinance;
    if (w === "captura" || w === "intimacoes") return !!data.controladoria;
    if (w === "metas" || w === "equipe_carga" || w === "captacao") return can("bi.ver");
    if (w === "processos_etapa") return can("bi.ver") && can("processo.ver");
    if (w === "parados") return can("processo.ver");
    if (w === "casos_mes") return can("caso.ver");
    return true;
  };
  const cur = edit ?? layout;
  const items = cur.items.filter((it) => available(it.id));
  const hidden = (Object.keys(WIDGET_LABEL) as WidgetId[]).filter((w) => w !== "kpis" && available(w) && !cur.items.some((it) => it.id === w));
  const setItems = (list: Item[]) => { if (edit) setEdit({ ...edit, items: list }); };
  const setOpts = (id: WidgetId, o: Item["o"]) => { if (edit) setEdit({ ...edit, items: edit.items.map((it) => (it.id === id ? { ...it, o } : it)) }); };
  const removeItem = (id: WidgetId) => { if (edit) setEdit({ ...edit, items: compact(edit.items.filter((it) => it.id !== id)) }); };
  const save = async (target: "me" | string) => {
    if (!edit) return;
    const body = { v: 3, items: edit.items.map(({ id, x, y, w, h, o }) => ({ id, x, y, w, h, ...(o ? { o } : {}) })) };
    try {
      if (target === "me") await api.put("/painel/layout", body);
      else await api.put(`/painel/layout/papel/${target}`, body);
      toast.ok(target === "me" ? "Início salvo" : "Padrão do papel publicado");
      await qc.invalidateQueries({ queryKey: ["painel-layout"] });
      if (target === "me") setEdit(null);
    } catch (e) { toast.error((e as Error).message); }
  };
  const reset = async () => {
    await api.del("/painel/layout");
    await qc.invalidateQueries({ queryKey: ["painel-layout"] });
    setEdit(null);
    toast.ok("Início voltou ao padrão");
  };

  const render = (w: WidgetId): JSX.Element | null => {
    switch (w) {
      case "alertas": return data.alerts.length || edit ? (
        <div className="alert-list">
          {data.alerts.map((a, i) => (
            <div key={i} className={`alert-item ${a.level}`} onClick={() => navigate(a.link)}>
              <Icon name={a.level === "vermelho" ? "alert" : "clock"} />
              <span style={{ flex: 1 }}>{a.text}</span>
              <strong className="small">Abrir →</strong>
            </div>
          ))}
          {!data.alerts.length && <div className="muted small">Alertas aparecem aqui quando houver.</div>}
        </div>
      ) : null;
      case "kpi_abertas": return <Kpi icon="check" tone="bb-brand" label="Minhas abertas" value={data.counts.mineOpen} hint={`${data.counts.mineToday} para hoje`} to="/atividades" />;
      case "kpi_atrasadas": return <Kpi icon="alert" tone="bb-red" label="Atrasadas" value={data.counts.mineOverdue} hint={data.counts.mineOverdue ? "resolver primeiro" : "tudo em dia"} to="/atividades?visao=atrasadas" bad={data.counts.mineOverdue > 0} />;
      case "kpi_fatais": return <Kpi icon="clock" tone="bb-amber" label="Prazos fatais na semana" value={data.fatalDeadlines.length} hint={`${data.fatalDeadlines.filter((a) => a.fatalDueAt?.slice(0, 10) === todayIso()).length} hoje`} to="/agenda?modo=dia" />;
      case "kpi_revisar": return <Kpi icon="tick" tone="bb-violet" label="Para eu revisar" value={data.counts.reviewQueue} hint={data.counts.unassigned ? `${data.counts.unassigned} na fila` : ""} to="/atividades?visao=revisao" />;
      case "kpis": return (
        <div className="grid cols-4">
          <div className="stat clickable" onClick={() => navigate("/atividades")}>
            <div className="stat-label"><span className="bubble bb-brand"><Icon name="check" /></span>Minhas abertas</div>
            <div className="stat-value">{data.counts.mineOpen}</div>
            <div className="stat-hint">{data.counts.mineToday} para hoje</div>
          </div>
          <div className={`stat clickable ${data.counts.mineOverdue ? "bad" : ""}`} onClick={() => navigate("/atividades?visao=atrasadas")}>
            <div className="stat-label"><span className="bubble bb-red"><Icon name="alert" /></span>Atrasadas</div>
            <div className="stat-value">{data.counts.mineOverdue}</div>
            <div className="stat-hint">{data.counts.mineOverdue ? "resolver primeiro" : "tudo em dia"}</div>
          </div>
          <div className="stat clickable" onClick={() => navigate("/agenda?modo=dia")}>
            <div className="stat-label"><span className="bubble bb-amber"><Icon name="clock" /></span>Prazos fatais na semana</div>
            <div className="stat-value">{data.fatalDeadlines.length}</div>
            <div className="stat-hint">{data.fatalDeadlines.filter((a) => a.fatalDueAt?.slice(0, 10) === todayIso()).length} hoje</div>
          </div>
          <div className="stat clickable" onClick={() => navigate("/atividades?visao=revisao")}>
            <div className="stat-label"><span className="bubble bb-violet"><Icon name="tick" /></span>Para eu revisar</div>
            <div className="stat-value">{data.counts.reviewQueue}</div>
            <div className="stat-hint">{data.counts.unassigned ? `${data.counts.unassigned} na fila sem responsável` : " "}</div>
          </div>
        </div>
      );
      case "fatais": return (
        <Card title="Prazos fatais — próximos 7 dias" actions={<button className="link-btn" onClick={() => navigate("/agenda")}>Ver agenda</button>}>
          {data.fatalDeadlines.length ? data.fatalDeadlines.map((a) => (
            <div key={a.id} className="lrow" onClick={() => navigate(`/atividades/${a.id}`)}>
              <DateChip iso={a.fatalDueAt!} />
              <div className="grow"><div className="tt">{a.title}</div><div className="mm">#{a.number}{a.typeKey ? ` · ${typeName(a.typeKey)}` : ""}</div></div>
              {a.responsibleUserId && <Avatar name={userName(a.responsibleUserId)} id={a.responsibleUserId} size={24} />}
              {a.state === "atribuida" && a.firstStartedAt ? <Badge tone="warn">⏸ Pausada</Badge> : <Badge tone={stateTone[a.state] ?? "neutral"}>{STATE_LABEL[a.state] ?? a.state}</Badge>}
            </div>
          )) : <Empty icon="tick">Nenhum prazo fatal nos próximos 7 dias.</Empty>}
        </Card>
      );
      case "carga_semana": return (
        <Card title="Carga da semana" actions={<span className="muted small"><span className="led" style={{ background: "var(--primary)", marginRight: 4 }} />minhas atividades · <span className="led" style={{ background: "var(--red)", margin: "0 4px" }} />prazos fatais</span>}>
          <WeekLoad items={data.mine} fatal={data.fatalDeadlines} />
        </Card>
      );
      case "minhas": return (
        <Card title="Minhas atividades" actions={<button className="link-btn" onClick={() => navigate("/atividades")}>Ver todas</button>}>
          {data.mine.slice(0, 8).map((a) => (
            <div key={a.id} className={`lrow ${a.state === "aguardando_dependencia" ? "waiting" : ""}`} onClick={() => navigate(`/atividades/${a.id}`)}>
              <Badge tone={stateTone[a.state] ?? "neutral"}>{STATE_LABEL[a.state] ?? a.state}</Badge>
              <div className="grow"><div className="tt">{a.title}</div><div className="mm">#{a.number}</div></div>
              {a.fatalDueAt ? <span className="text-bad small">⚑ {a.fatalDueAt.slice(8, 10)}/{a.fatalDueAt.slice(5, 7)}</span>
                : a.relatedFatal && <span className="small"><FatalDate related={a.relatedFatal} short /></span>}
              {a.internalDueAt && <span className={`small ${isOverdue(a.internalDueAt) ? "text-bad" : "muted"}`}>{a.internalDueAt.slice(8, 10)}/{a.internalDueAt.slice(5, 7)}</span>}
            </div>
          ))}
          {!data.mine.length && <Empty icon="tick">Tudo em dia.</Empty>}
        </Card>
      );
      case "audiencias": return (
        <Card title="Audiências">
          {data.hearings.length ? data.hearings.map((a) => (
            <div key={a.id} className="lrow" onClick={() => navigate(`/atividades/${a.id}`)}>
              <DateChip iso={(a.plannedAt ?? a.fatalDueAt ?? a.internalDueAt)!} />
              <div className="grow"><div className="tt">{a.title}</div><div className="mm">{userName(a.responsibleUserId)}</div></div>
            </div>
          )) : <Empty icon="cal">Nenhuma audiência esta semana.</Empty>}
        </Card>
      );
      case "captura": return data.controladoria ? (
        <Card title="Captura de publicações" actions={<button className="link-btn" onClick={() => navigate("/controladoria")}>Controladoria</button>}>
          <div className="stack" style={{ gap: 8 }}>
            <div className="row"><span className={`led ${data.controladoria.captureOk ? "g" : "r"}`} />DJEN<span className="spacer" /><span className="muted small">{data.controladoria.captureOk ? `ok · ${fmtDateTime(data.controladoria.lastOkAt)}` : "com falha"}</span></div>
            <div className="row"><span className="led o" />DataJud (conferência)<span className="spacer" /><span className="muted small">ver Controladoria</span></div>
            <hr className="sep" style={{ margin: "4px 0" }} />
            <div className="row between"><span>Não analisadas</span><strong>{data.controladoria.pending}</strong></div>
            <div className="row between"><span>Há mais de 48h</span><strong className={data.controladoria.late ? "text-bad" : ""}>{data.controladoria.late}</strong></div>
          </div>
        </Card>
      ) : null;
      case "aniversarios": return (
        <Card title="Aniversariantes da semana">
          {data.birthdays.length ? data.birthdays.map((b) => {
            const d = b.birthDate.slice(5);
            const today = todayIso().slice(5) === d;
            return (
              <div key={b.id} className="lrow" onClick={() => navigate(`/pessoas/${b.id}`)}>
                <Avatar name={b.name} id={b.id} size={24} />
                <div className="grow"><div style={{ fontWeight: 500 }}>{b.name}</div></div>
                <span className="muted small">{today ? "hoje 🎂" : `${d.slice(3)} ${MES[Number(d.slice(0, 2)) - 1]}`}</span>
              </div>
            );
          }) : <Empty icon="user">Ninguém nesta semana.</Empty>}
        </Card>
      );
      case "financeiro": return data.canSeeFinance ? (
        <Card title="Financeiro" actions={<button className="btn small" onClick={() => setShowFin(!showFin)}>{showFin ? "Ocultar" : "Mostrar"}</button>}>
          {!showFin && <div className="muted small">Oculto por padrão.</div>}
          {showFin && data.financeiro && (
            <div className="stack" style={{ gap: 8 }}>
              <div className="row between"><span>A receber vencido</span><strong className="text-bad">{fmtMoney(data.financeiro.receivableOverdue)}</strong></div>
              <div className="muted small">{data.financeiro.receivableOverdueCount} parcela(s)</div>
              <div className="row between"><span>A pagar em 7 dias</span><strong>{fmtMoney(data.financeiro.payableNext7)}</strong></div>
              <button className="btn" onClick={() => navigate("/financeiro")}>Abrir Financeiro</button>
            </div>
          )}
        </Card>
      ) : null;
      case "metas": return <MetasWidget />;
      case "compromissos": return blocks ? <AgendaCard agenda={blocks.agenda} today={blocks.today} /> : <Card title="Meus compromissos"><Loading /></Card>;
      case "tarefas": return blocks ? <TasksCard tasks={blocks.tasks} today={blocks.today} /> : <Card title="Tarefas em aberto"><Loading /></Card>;
      case "calendario": return <CalendarWidget />;
      case "acesso_rapido": return <QuickCard />;
      case "dica": return <TipCard today={todayIso()} />;
      case "equipe_carga": return <TeamLoadWidget />;
      case "processos_etapa": return <StagesWidget />;
      case "postits": return <PostitsWidget />;
      case "chat": return <ChatWidget />;
      case "fluxos": return <Card title="Fluxos em andamento" actions={<button className="link-btn" onClick={() => navigate("/fluxos?aba=andamento")}>Ver todos</button>}><FlowsInProgress compact mine /></Card>;
      case "pontuacao": return <ScoreRanking from={`${todayIso().slice(0, 8)}01`} to={todayIso()} compact />;
      default: return QUERY_WIDGETS[w] ? <QueryWidget id={w} o={cur.items.find((it) => it.id === w)?.o} /> : null;
    }
  };

  return (
    <div className="stack gap-16">
      <div className="page-header">
        <div>
          <h1>Olá, {first}!</h1>
          <div className="muted">{greeting()}! Aqui está o resumo do seu dia — <span style={{ textTransform: "lowercase" }}>{now.toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}</span>.</div>
        </div>
        <div className="row">
          {!edit && <button className="btn ghost" onClick={() => setEdit({ v: 3, items: layout.items.map((it) => ({ ...it })) })} title="Escolher, mover e redimensionar os quadros"><Icon name="gear" size="sm" />Personalizar</button>}
          <button className="btn" onClick={() => navigate("/agenda")}><Icon name="cal" size="sm" />Minha agenda</button>
          <button className="btn primary" onClick={() => navigate("/atividades?nova=1")}><Icon name="plus" size="sm" />Nova atividade</button>
        </div>
      </div>

      {edit && (
        <div className="card compact">
          <div className="row" style={{ flexWrap: "wrap", gap: 8 }}>
            <b>Personalizando o Início</b>
            <span className="muted small">Arraste pela barra do quadro para mover (ou use as setas); puxe o canto ◢ para qualquer tamanho; P/M/G são tamanhos prontos.</span>
            <span className="grow" />
            {hidden.length > 0 && (
              <select className="input" style={{ width: 240 }} value="" onChange={(e) => { const w = e.target.value as WidgetId; if (w) { const [cw, ch] = DEFAULT_SIZE(w); setEdit({ ...edit, items: [...edit.items, { id: w, x: 0, y: bottom(edit.items), w: cw, h: ch }] }); } }}>
                <option value="">+ Adicionar quadro…</option>
                {[...new Set(hidden.map(WIDGET_GROUP))].map((g) => (
                  <optgroup key={g} label={g}>{hidden.filter((w) => WIDGET_GROUP(w) === g).map((w) => <option key={w} value={w}>{WIDGET_LABEL[w]}</option>)}</optgroup>
                ))}
              </select>
            )}
            <button className="btn" onClick={() => setEdit(null)}>Cancelar</button>
            <button className="btn ghost" onClick={() => void reset()}>Restaurar padrão</button>
            <button className="btn primary" onClick={() => void save("me")}>Salvar meu Início</button>
          </div>
          {can("config.gerenciar") && (
            <div className="row" style={{ marginTop: 8, gap: 8 }}>
              <span className="muted small">Administrador: publicar este arranjo como padrão do papel</span>
              <select className="input" style={{ width: 180 }} value={roleTarget} onChange={(e) => setRoleTarget(e.target.value)}>
                <option value="">Escolha o papel…</option>
                {(catalog?.roles ?? []).map((r) => <option key={r.key} value={r.key}>{r.name}</option>)}
              </select>
              <button className="btn small" disabled={!roleTarget} onClick={() => void save(roleTarget)}>Publicar padrão</button>
              <span className="muted small">Vale para quem ainda não personalizou o próprio Início.</span>
            </div>
          )}
        </div>
      )}

      <DashGrid items={items} all={cur.items} edit={!!edit} render={render} onChange={setItems} onRemove={removeItem} onOpts={setOpts} />
    </div>
  );
}

// ------------------------------------------------------------ grade do Início

/** Número do dia em formato compacto (uma linha). */
function Kpi({ icon, tone, label, value, hint, to, bad }: { icon: string; tone: string; label: string; value: number; hint: string; to: string; bad?: boolean }): JSX.Element {
  return (
    <div className={`kpi-mini ${bad ? "bad" : ""}`} onClick={() => navigate(to)} role="button">
      <span className={`bubble ${tone}`}><Icon name={icon} /></span>
      <b className="kpi-v">{value}</b>
      <span className="kpi-l">{label}{hint && <small>{hint}</small>}</span>
    </div>
  );
}

function DashGrid({ items, all, edit, render, onChange, onRemove, onOpts }: {
  items: Item[]; all: Item[]; edit: boolean; render: (w: WidgetId) => JSX.Element | null;
  onChange: (list: Item[]) => void; onRemove: (id: WidgetId) => void; onOpts: (id: WidgetId, o: Item["o"]) => void;
}): JSX.Element {
  const grid = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState<WidgetId | null>(null);
  const [optsFor, setOptsFor] = useState<WidgetId | null>(null);

  /** Arrastar (mover) ou puxar o canto (redimensionar), sempre encaixando na grade. */
  const start = (e: React.PointerEvent, it: Item, mode: "move" | "size") => {
    if (!edit || (e.target as HTMLElement).closest("button,select,input")) return;
    e.preventDefault(); e.stopPropagation();
    const g = grid.current!.getBoundingClientRect();
    const colW = (g.width + GAP) / COLS, rowH = ROW + GAP;
    const x0 = e.clientX, y0 = e.clientY;
    setActive(it.id);
    let last = "";
    const move = (ev: PointerEvent) => {
      const dc = Math.round((ev.clientX - x0) / colW), dr = Math.round((ev.clientY - y0) / rowH);
      const next = mode === "move" ? { ...it, x: it.x + dc, y: Math.max(0, it.y + dr) } : { ...it, w: Math.max(2, Math.min(COLS - it.x, it.w + dc)), h: Math.max(2, it.h + dr) };
      const key = `${next.x},${next.y},${next.w},${next.h}`;
      if (key === last) return;
      last = key;
      onChange(resolve(all.map((i) => (i.id === it.id ? next : i)), it.id));
    };
    const up = () => { setActive(null); window.removeEventListener("pointermove", move); window.removeEventListener("pointerup", up); };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  const sorted = [...items].sort((a, b) => a.y - b.y || a.x - b.x);
  return (
    <div className={`dash-grid ${edit ? "editing" : ""}`} ref={grid}>
      {sorted.map((it) => {
        const node = render(it.id);
        if (!node && !edit) return null;
        const qw = QUERY_WIDGETS[it.id];
        return (
          <div key={it.id} className={`dash-item ${active === it.id ? "active" : ""}`}
            style={{ gridColumn: `${it.x + 1} / span ${it.w}`, gridRow: `${it.y + 1} / span ${it.h}`, ["--h" as string]: `${px(it.h)}px` }}>
            {edit && (
              <div className="dash-bar" onPointerDown={(e) => start(e, it, "move")} title="Arraste para mover">
                <span className="dash-name">⠿ {WIDGET_LABEL[it.id]}</span>
                <span className="dash-btns">
                  <button title="Para cima" onClick={() => onChange(nudge(all, it.id, "up"))}>↑</button>
                  <button title="Para baixo" onClick={() => onChange(nudge(all, it.id, "down"))}>↓</button>
                  <button title="Para a esquerda" onClick={() => onChange(nudge(all, it.id, "left"))}>←</button>
                  <button title="Para a direita" onClick={() => onChange(nudge(all, it.id, "right"))}>→</button>
                  {PRESETS.map(([k, l, w, h]) => <button key={k} title={`${l}`} onClick={() => onChange(resolve(all.map((i) => (i.id === it.id ? { ...i, w: SMALL.includes(i.id) && k === "P" ? 6 : w, h: SMALL.includes(i.id) ? 2 : h, x: Math.min(i.x, COLS - w) } : i)), it.id))}>{k}</button>)}
                  {qw?.opts && <button title="Opções" onClick={() => setOptsFor(optsFor === it.id ? null : it.id)}>⚙</button>}
                  <button title="Tirar do Início" onClick={() => onRemove(it.id)}>✕</button>
                </span>
                {optsFor === it.id && qw?.opts && (
                  <div className="dash-opts">
                    {qw.opts.values.map(([v, l]) => (
                      <button key={v} className={String(it.o?.[qw.opts!.name] ?? "") === v ? "on" : ""} onClick={() => { onOpts(it.id, { ...(it.o ?? {}), [qw.opts!.name]: v }); setOptsFor(null); }}>{l}</button>
                    ))}
                  </div>
                )}
              </div>
            )}
            <div className="dash-body">{node ?? <div className="card muted small">Sem dados para você neste quadro.</div>}</div>
            {edit && <span className="dash-resize" title="Puxe para mudar o tamanho" onPointerDown={(e) => start(e, it, "size")}>◢</span>}
          </div>
        );
      })}
    </div>
  );
}
